import { describeError, logger } from '@/lib/log'

/**
 * Screens are unattended, so a deploy has to land on them without anyone
 * touching a remote. `version.json` is emitted by the build and polled here;
 * when the build id changes the page reloads itself.
 *
 * Requires `index.html` and `version.json` to be served with `no-store`
 * (see Docs/deployment.md) — otherwise the screen reloads into a cached bundle.
 */
export function useSelfUpdate(pollMs: number) {
  let timer = 0
  let running = false
  let reloading = false

  const url = `${import.meta.env.BASE_URL}version.json`

  async function check(): Promise<void> {
    if (!running || reloading) return
    try {
      const response = await fetch(`${url}?r=${Date.now()}`, {
        cache: 'no-store',
        credentials: 'omit',
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const payload = (await response.json()) as { buildId?: unknown }
      const buildId = typeof payload.buildId === 'string' ? payload.buildId : null
      if (buildId && buildId !== __BUILD_ID__) {
        reloading = true
        logger.info(`New build ${buildId} detected (running ${__BUILD_ID__}) — reloading`)
        window.location.reload()
        return
      }
    } catch (caught) {
      // Harmless in dev, where version.json is not generated.
      logger.debug(`Version check failed: ${describeError(caught)}`)
    }
    timer = window.setTimeout(() => void check(), pollMs)
  }

  function start(): void {
    if (running) return
    running = true
    timer = window.setTimeout(() => void check(), pollMs)
  }

  function stop(): void {
    running = false
    window.clearTimeout(timer)
  }

  return { start, stop, check }
}
